import { Fabric3SidebarItem } from "@granular/fabric3-definitions";

export const SIDEBAR_VISIBILITY_FLAGS = {
  0: "NOT_VISIBLE_NOT_ROUTABLE",
  1: "VISIBLE_NOT_ROUTABLE",
  2: "VISIBLE_ROUTABLE",
  3: "NOT_VISIBLE_ROUTABLE",
} as const;

type SidebarVisibilityFlagNum = keyof typeof SIDEBAR_VISIBILITY_FLAGS;

export type SidebarItemVisibility = {
  visible: boolean;
  routable: boolean;
};

/**
 * Function to get a feature flag key for an app sidebar item.
 *
 * @param namespace - The app namespace i.e. the app/_____ folder
 * @param sidebar - Sidebar config
 * @returns
 */
export const getFeatureFlagKey = (
  namespace: string,
  sidebarRoute: Fabric3SidebarItem["route"],
): string => {
  return `app-${namespace}${sidebarRoute ? `-${sidebarRoute}` : ""}`;
};

/**
 * Checks the LD flag determining app visibility/routability for some namespace and sidebar item.
 * Defaults to not visible/not routable if flag is missing or invalid.
 * @param flags - The feature flags record
 * @param namespace - The app namespace i.e. the app/_____ folder
 * @param sidebarRoute - Sidebar config item route
 * @returns The visibility/routability of the sidebar item
 */
export const getSidebarVisibility = (
  flags: Record<string, SidebarVisibilityFlagNum>,
  namespace: string,
  sidebarRoute?: Fabric3SidebarItem["route"],
): SidebarItemVisibility => {
  const featureFlagKey = getFeatureFlagKey(namespace, sidebarRoute);
  const flagNum = flags[featureFlagKey];
  // Should never happen, but if we get an enum value outside the expected range we should squawk.
  if (flagNum !== undefined && !(flagNum in SIDEBAR_VISIBILITY_FLAGS)) {
    console.error(
      `While checking app visibility flag for ${featureFlagKey}, got invalid flag value: ${flagNum}`,
    );
  }
  const flag =
    flagNum === undefined ? undefined : SIDEBAR_VISIBILITY_FLAGS[flagNum];
  return {
    visible: flag === "VISIBLE_NOT_ROUTABLE" || flag === "VISIBLE_ROUTABLE",
    routable: flag === "VISIBLE_ROUTABLE" || flag === "NOT_VISIBLE_ROUTABLE",
  };
};
